import { useEffect, useRef, useState } from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import { RxChevronRight, RxHome } from 'react-icons/rx';
import useGetSelectedBusiness from '../../hooks/useGetSelectedBusiness';
import useGetSelectedEvent from '../../hooks/useGetSelectedEvent';
import useNavigateWithQuery from '../../hooks/useNavigateWithQuery';
import useIsMobile from '../../hooks/useIsMobile';
import LoadingText from './LoadingText';

const breadcrumbNameMap: { [key: string]: string } = {
  admin: 'Inicio',
  dashboard: 'Dashboard',
  businesses: 'Productoras',
  orders: 'Asistentes',
  events: 'Eventos',
  tickets: 'Entradas',
  'sales-report': 'Reportes',
  coupons: 'Cupones',
  referrals: 'Referidos',
  sellers: 'Vendedores',
  manual: 'Venta manual',
  'entry-data': 'Datos de entrada',
  checkout: 'Checkout',
  details: 'Detalles',
  edit: 'Editar evento',
  create: 'Crear evento',
  'create-business': 'Crear productora',
  'create-event': 'Crear evento',
  'create-ticket': 'Crear ticket',
  balance: 'Balance',
  scanner: 'Escaner',
  inperson: 'Venta presencial',
  'edit-business': 'Editar productora',
  users: 'Usuarios',
  invitations: 'Invitaciones',
  mercadopago: 'Mercado Pago',
  superadmin: 'Panel de gestión',
  discounts: 'Descuentos',
  'cash-register': 'Cajas',
  members: 'Socios',
  'all-events': 'Eventos',
  'simplified-manual': 'Venta rápida',
  showtimes: 'Funciones',
  customers: 'CRM - Gestión de clientes',
  clients: 'Clientes',
  groups: 'Grupos',
  'order-referrals': 'Compradores promotores',
  lists: 'Listas',
  guests: 'Invitados',
  validator: 'Validador',
  polls: 'Encuestas',
  list: 'Lista',
  scanning: 'Escanear códigos',
  'sales-report-pdf': 'Reporte',
  'scanner-types': 'Tipos de escáner',
};

const IconBreadcrumbs = () => {
  const location = useLocation();
  const [maxItem, setMaxItem] = useState(2);
  const navigate = useNavigateWithQuery();
  const [open, setOpen] = useState(false);
  const ellipsisRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    setMaxItem(isMobile ? 2 : 10);
  }, [isMobile]);

  const pathnames = location.pathname.split('/').filter((x) => x);

  let businessId: string | null = null;
  let eventId: string | null = null;

  if (pathnames.length >= 2 && pathnames[0] === 'admin') {
    businessId = pathnames[1].match(/^[a-f0-9-]{36}$/) ? pathnames[1] : null;
  }
  if (pathnames.length >= 4 && businessId && pathnames[2] === 'events') {
    eventId = pathnames[3].match(/^[a-f0-9-]{36}$/) ? pathnames[3] : null;
  }

  const { business, businessLoading } = useGetSelectedBusiness();
  const { event, eventLoading } = useGetSelectedEvent();

  const isUUID = (str: string) => /^[a-f0-9-]{36}$/.test(str);

  const filteredPathnames = pathnames.filter((path) => {
    if (['events', 'new', 'order-success', 'order-error', 'onboarding'].includes(path)) {
      return false;
    }

    if (path !== businessId && path !== eventId && isUUID(path)) {
      return false;
    }

    return true;
  });

  const links = filteredPathnames.map((path) => {
    const originalIndex = pathnames.indexOf(path);
    let url = `/${pathnames.slice(0, originalIndex + 1).join('/')}`;
    let name: string | JSX.Element = breadcrumbNameMap[path] || path;

    if (path === businessId) {
      if (businessLoading) {
        name = <LoadingText isLoading text="" className="w-20 h-4" />;
      } else if (business) {
        name = (business.name && business.name.length > 30)
          ? `${business.name.substring(0, 30)}...`
          : business.name || 'Productora';
        url += '/events';
      } else {
        name = 'Productora'; // Fallback if not loading and no business
        url += '/events';
      }
    } else if (path === eventId) {
      if (eventLoading) {
        name = <LoadingText isLoading text="" className="w-20 h-4" />;
      } else if (event) {
        name = (event.name && event.name.length > 30)
          ? `${event.name.substring(0, 30)}...`
          : event.name || 'Evento';
      } else {
        name = 'Evento'; // Fallback if not loading and no event
      }
    } else if (path === 'list') {
      // For 'list' breadcrumb, include the list_id in the URL if it exists
      const nextIndex = originalIndex + 1;
      if (nextIndex < pathnames.length && isUUID(pathnames[nextIndex])) {
        url = `/${pathnames.slice(0, nextIndex + 1).join('/')}`;
      }
    }

    return { name, link: url };
  });

  const handleEllipsisClick = () => {
    setOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (domEvent: MouseEvent) => {
      const target = domEvent.target as Node;
      if (open && menuRef.current && !menuRef.current.contains(target) && ellipsisRef.current && !ellipsisRef.current.contains(target)) {
        setOpen(false);
      }
    };
    const handleEscape = (domKeyEvent: KeyboardEvent) => {
      if (domKeyEvent.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const shouldCollapse = links.length > maxItem;
  const last = links[links.length - 1];
  const middle = links.slice(1, -1);

  return (
    <div ref={wrapperRef} className='pl-4 mt-10 relative bg-blur-sm bg-opacity-medium rounded-3xl h-fit w-fit p-2 pr-4 z-10'>
      <nav aria-label="breadcrumb" className="flex items-center gap-2 text-sm">
        <RouterLink to="/admin" className="text-text-tertiary hover:text-text-primary flex items-center">
          <RxHome />
        </RouterLink>
        <RxChevronRight className="text-text-tertiary" />
        {!shouldCollapse && (
          <div className="flex items-center gap-2 flex-wrap">
            {links.slice(1).map((link, idx) => (
              <div key={link.link} className="flex items-center gap-2">
                <RouterLink
                  to={link.link}
                  className="text-text-tertiary hover:text-text-primary font-[Rubik] text-[14px] flex items-center"
                >
                  {link.name}
                </RouterLink>
                {idx !== links.slice(1).length - 1 && <RxChevronRight className="text-text-tertiary" />}
              </div>
            ))}
          </div>
        )}
        {shouldCollapse && (
          <>
            <button
              ref={ellipsisRef}
              type="button"
              onClick={handleEllipsisClick}
              className="flex items-center justify-center px-1 cursor-pointer text-text-tertiary bg-transparent"
              aria-haspopup="menu"
              aria-expanded={open}
              aria-label="Mostrar rutas intermedias"
            >
              ...
            </button>
            <RxChevronRight className="text-text-tertiary" />
            <RouterLink
              to={last.link}
              className="text-text-tertiary hover:text-text-primary font-[Rubik] text-[14px] flex items-center"
            >
              {last.name}
            </RouterLink>
            {open && (
              <div
                ref={menuRef}
                role="menu"
                className="absolute z-[1000] bg-surface-base border border-border-secondary rounded-2xl p-1 shadow-lg text-text-primary"
                style={{ top: 36, left: 40 }}
              >
                <div className="flex flex-col min-w-[180px] max-h-[320px] overflow-auto">
                  {middle.map((link) => (
                    <button
                      key={link.link}
                      type="button"
                      onClick={() => { setOpen(false); navigate(link.link); }}
                      className="text-left px-3 py-2 rounded-xl hover:bg-surface-secondary text-text-primary"
                      role="menuitem"
                    >
                      {link.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </nav>
    </div>
  );
};

export default IconBreadcrumbs;
